import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * Vald kommun delas mellan admin-sidorna (Uppdrag, tabellvyn, Utdelare) i stället för att
 * varje sida håller sin egen — man rör sig ofta mellan dem och vill inte välja om varje gång.
 *
 * Värdet är ett MunicipalityPolygonId. Det ligger i localStorage (överlever omladdning och
 * nya sessioner) och speglas i `?municipality=` så att en länk eller ett bokmärke bär med sig
 * valet. URL:en vinner vid inläsning, så en delad länk pekar alltid på rätt kommun.
 */
const STORAGE_KEY = "centerreach.admin.selectedMunicipality";
const URL_PARAM = "municipality";

const readStored = (): string => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) ?? "";
    } catch {
        // Privat läge / blockerad storage — kör vidare utan att komma ihåg valet.
        return "";
    }
};

const readFromUrl = (): string => new URLSearchParams(window.location.search).get(URL_PARAM) ?? "";

let currentValue = readFromUrl() || readStored();
const listeners = new Set<() => void>();

const writeStored = (value: string): void => {
    try {
        if (value) {
            window.localStorage.setItem(STORAGE_KEY, value);
        } else {
            window.localStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // Se readStored.
    }
};

const writeUrl = (value: string): void => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(URL_PARAM) === (value || null)) {
        return;
    }
    if (value) {
        params.set(URL_PARAM, value);
    } else {
        params.delete(URL_PARAM);
    }
    const query = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
};

export const getSelectedMunicipalityId = (): string => currentValue;

export const setSelectedMunicipalityId = (value: string): void => {
    writeUrl(value);
    if (value === currentValue) {
        return;
    }
    currentValue = value;
    writeStored(value);
    for (const listener of listeners) {
        listener();
    }
};

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/**
 * Returnerar [kommun-id, sätt-kommun]. Vid montering skrivs ett redan valt värde ut i URL:en,
 * så att adressfältet på sidan man landar på matchar det som visas.
 */
export const useSelectedMunicipalityId = (): [string, (value: string) => void] => {
    const value = useSyncExternalStore(subscribe, getSelectedMunicipalityId, getSelectedMunicipalityId);

    const setValue = useCallback((next: string) => {
        setSelectedMunicipalityId(next);
    }, []);

    // Sidan kan ha nåtts utan query-parametern (t.ex. via menyn) — spegla valet dit.
    useEffect(() => {
        if (value) {
            writeUrl(value);
        }
    }, [value]);

    return [value, setValue];
};
